/**
 * How a measurement total reads — `docs/property-maps-v2.md` §9.4.
 *
 * One function, because three surfaces print the same thing: the Empty
 * screen's *Already measured* strip, the phone card's measurements line, and
 * the Measurements panel's group headers. Two of them disagreed within a day
 * of being written — one printed `28,171.772` and the other the raw number
 * with no unit at all.
 *
 * **The type's own unit, never rolled up.** `formatArea` turns square feet
 * into acres past a quarter-acre, which is right for a shape somebody is
 * drawing and wrong for a stored total in a unit the company chose: a turf
 * figure reading `0.28 ac` here and `12,400 sq ft` on the Overview card is
 * two answers to one question.
 *
 * **How round it gets follows the size of the unit, not its family.** A
 * square foot is small enough that a fraction of one means nothing, so
 * `numeric(14,3)`'s three decimals are noise — `28,171.772 sf` is a figure
 * nobody asked for. An acre is not: `1.5 ac` is half an acre of snow lot, and
 * rounding it to `2 ac` is a lie about the site. So feet round to whole and
 * everything else keeps up to two decimals.
 */
import { formatNumber } from '../lib/format.js'

export interface MeasuredGroup { type: string; unit: string; total: number }

/** The units small enough that a fraction of one is not worth printing. */
const WHOLE = new Set([
  'sq ft', 'sqft', 'sq. ft.', 'sf', 'square feet',
  'lin ft', 'linft', 'lf', 'linear feet', 'ft', 'feet',
])

export function measurementValue(group: Pick<MeasuredGroup, 'unit' | 'total'>): string {
  const value = WHOLE.has(group.unit.trim().toLowerCase())
    ? Math.round(group.total)
    : Number(group.total.toFixed(2))
  return `${formatNumber(value)} ${group.unit}`
}

/** `Turf 12,400 sq ft` — the whole phrase, for a dot-separated list. */
export const measurementText = (group: MeasuredGroup): string =>
  `${group.type} ${measurementValue(group)}`
